import type {ReactElement} from 'react';
import {useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {ShareInTeamToggleBar} from '@Components/share-in-team-toggle-bar/share-in-team-toggle-bar';
import {ShareInTeamBarState} from '@Components/share-in-team-toggle-bar/share-in-team-toggle-bar.types';
import {shareEmailMarketingCampaignInTeam} from '@Libraries/email-campaigns-library';
import {isUserPremium} from '@Libraries/user.library';
import {CollaborationGA4Source, CollaborationGA4Toggle, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {useAppDispatch} from '@/hooks';
import {setEmailMakerIsSharedInTeam} from '@EmailMakerReact/reducers/email-maker-publish-state-reducer';
import type {EmailMakerShareInTeamPopupProps} from './email-maker-share-in-team-popup.types';
import styles from './email-maker-share-in-team-popup.module.scss';

export function EmailMakerShareInTeamPopup({selector, idEmailMarketingCampaign, isSharedInTeam}: EmailMakerShareInTeamPopupProps): ReactElement {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  function resolveToggleBarState(): ShareInTeamBarState {
    if (!isUserPremium()) {
      return ShareInTeamBarState.UPSELLING;
    }
    return isSharedInTeam ? ShareInTeamBarState.ACTIVE : ShareInTeamBarState.INACTIVE;
  }

  function onStateUpdate(next: ShareInTeamBarState): void {
    if (next === ShareInTeamBarState.UPSELLING) {
      return;
    }
    const nextIsShared = next === ShareInTeamBarState.ACTIVE;
    void runShareToggle(idEmailMarketingCampaign, nextIsShared, setIsLoading, (value) => {
      dispatch(setEmailMakerIsSharedInTeam(value));
    });
  }

  return (
    <Dropdown
      selector={selector}
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      popUpHasCustomWidth
      popup={
        <div className={styles.popupWrapper}>
          <ShareInTeamToggleBar state={resolveToggleBarState()} isCurrentlyLoading={isLoading} onStateUpdate={onStateUpdate} />
        </div>
      }
    />
  );
}

async function runShareToggle(
  idEmailMarketingCampaign: string,
  nextIsShared: boolean,
  setIsLoading: (next: boolean) => void,
  setIsSharedInTeamState: (next: boolean) => void
): Promise<void> {
  setIsLoading(true);
  try {
    await shareEmailMarketingCampaignInTeam(nextIsShared, idEmailMarketingCampaign);
    setIsSharedInTeamState(nextIsShared);
    trackShareToggle(nextIsShared);
  } catch (err: unknown) {
    window.PMW.log('Share in team toggle failed', err);
  } finally {
    setIsLoading(false);
  }
}

function trackShareToggle(isSharedInTeam: boolean): void {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLAB_EMAIL_SHAREINTEAM, {
    [GA4EventParamName.TYPE]: isSharedInTeam ? CollaborationGA4Toggle.SHARED : CollaborationGA4Toggle.UNSHARED,
    [GA4EventParamName.SOURCE]: CollaborationGA4Source.EMAIL_EDITOR,
  });
}
